import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useStoreon } from "storeon/react";

import {
  chatMessagesGet,
  chatMessagePost,
  sendGroupNotification,
} from "services/database";

import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Avatar from "@material-ui/core/Avatar";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";

import SendIcon from "@material-ui/icons/Send";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    display: "block",
    overflow: "hidden",
    textAlign: "center",
  },
  inline: {
    display: "inline",
  },
  chatWrapper: {
    alignItems: "flex-end",
    padding: "0 16px",
  },
  chatWrapperOwn: {
    textAlign: "right",
  },
  chatAvatar: {
    marginBottom: 16,
  },
  chatName: {
    color: "#bababa",
    fontSize: "0.8em",
    marginLeft: 5,
    marginRight: 5,
  },
  chatDate: {
    display: "block",
    marginTop: 2,
    color: "#c2c2c2",
    marginLeft: 5,
    marginRight: 5,
    fontSize: "0.7em",
  },
  chatText: {
    backgroundColor: "#f1f0f0",
    color: "rgba(0, 0, 0, 1)",
    margin: "1px 0",
    padding: "6px 12px",
    display: "inline-block",
    borderRadius: 15,
  },
  chatTextOwn: {
    backgroundColor: "#6699cc",
    color: "white",
  },
  chatArea: {
    position: "relative",
    width: "100%",
    maxHeight: 130,
    overflow: "auto",
  },
  chatInput: {
    width: "100%",
    paddingLeft: 40,
    paddingRight: 40,
    paddingBottom: 20,
    paddingTop: 20,
  },
}));

export default ({ chatId }) => {
  const classes = useStyles();
  const { dispatch, auth } = useStoreon("auth");
  const messagesList = useRef(null);
  const [chatMessage, setChatMessage] = useState("");
  const [newMessages, setNewMessages] = useState([]);
  const [lastKey, setLastKey] = useState(null);
  const [showLoadMore, setShowLoadMore] = useState(true);

  useEffect(() => {
    let messagesDB;
    if (chatId) {
      setTimeout(() => {
        messagesDB = chatMessagesGet(chatId, null);
        let i = 0;
        messagesDB.on("child_added", (snapshot) => {
          i++;
          var message = snapshot.val();
          setNewMessages((m) => [...m, { ...message, id: snapshot.key }]);
          setTimeout(() => scrollToBottom(), 100);
          if (i === 1) {
            setLastKey(snapshot.key);
          }
        });
      }, 300);
    }

    return () => {
      messagesDB && messagesDB.off("child_added");
    };
  }, [chatId]);

  const sendMessage = (ev) => {
    if ((ev === 1 || ev.charCode === 13) && chatMessage.trim() !== "") {
      chatMessagePost({
        chatId,
        user: auth.user,
        message: chatMessage,
      });
      setChatMessage("");
      setTimeout(() => scrollToBottom(), 100);
      // Also send notifcation:
      sendGroupNotification(
        auth.user.userID,
        auth.group,
        `${auth.user.firstname} sent a comment`,
        `"${chatMessage.slice(0, 20)}..."`
      );
    }
  };

  const loadMoreMessages = () => {
    dispatch("ui/setLoading", true);
    const messagesDB = chatMessagesGet(chatId, lastKey);
    messagesDB.once("value", (snapshot) => {
      const olderMessages = [];
      snapshot.forEach((child) => {
        if (!newMessages.find((m) => m.id === child.key)) {
          var message = child.val();
          olderMessages.unshift({ ...message, id: child.key });
        }
      });
      if (olderMessages.length > 0) {
        olderMessages.reverse();
        setLastKey(olderMessages[0].id);
        setNewMessages((m) => [...olderMessages, ...m]);
      } else {
        setShowLoadMore(false);
      }
      dispatch("ui/setLoading", false);
    });
  };

  const scrollToBottom = () => {
    const list = messagesList && messagesList.current;
    if (list) {
      const scrollHeight = list.scrollHeight;
      const height = list.clientHeight;
      const maxScrollTop = scrollHeight - height;
      list.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
    }
  };

  return (
    <div className={classes.root}>
      <List className={classes.chatArea} dense={true} ref={messagesList}>
        {showLoadMore && (
          <Button
            onClick={loadMoreMessages}
            style={{ textAlign: "center", textTransform: "none" }}
          >
            - Load previous messages -
          </Button>
        )}
        {newMessages.map((mess) => (
          <ListItem
            key={mess.id}
            dense={true}
            className={`${classes.chatWrapper} ${
              mess.user.userID === auth.user.userID && classes.chatWrapperOwn
            }`}
          >
            {mess.user.userID !== auth.user.userID && (
              <ListItemAvatar className={classes.chatAvatar}>
                <Avatar
                  alt="Remy Sharp"
                  src={mess.user.blob || mess.user.pic}
                />
              </ListItemAvatar>
            )}
            <ListItemText
              primary={
                mess.user.userID !== auth.user.userID && (
                  <Typography
                    component="span"
                    variant="body2"
                    className={classes.chatName}
                  >
                    {mess.user.firstname} {mess.user.lastname}
                  </Typography>
                )
              }
              secondary={
                <React.Fragment>
                  <span
                    className={`${classes.chatText} ${
                      mess.user.userID === auth.user.userID &&
                      classes.chatTextOwn
                    }`}
                  >
                    {mess.text}
                  </span>
                  <span className={classes.chatDate}>
                    {new Date(mess.added).toDateString()} -{" "}
                    {new Date(mess.added).toLocaleTimeString()}
                  </span>
                </React.Fragment>
              }
            />
          </ListItem>
        ))}
      </List>
      <TextField
        autoFocus={false}
        rowsMax={3}
        placeholder="Add comment..."
        onChange={(e) => setChatMessage(e.target.value)}
        value={chatMessage}
        onKeyPress={sendMessage}
        className={classes.chatInput}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => sendMessage(1)}>
                <SendIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </div>
  );
};
